import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { Status } from '@thuas/pd-schemas/built/generated/api-types';
import { RootState } from 'app/store';
import { addFileCases } from 'features/files/fileSlice';
import { ILibrary } from 'features/folders/folderAPI';
import {
  addFolderCases,
  fetchFoldersAsync,
} from 'features/folders/folderSlice';
import { IMenu } from 'features/menu/menuAPI';
import { addMenuItemCases } from 'features/menu/menuItemSlice';
import { addMenuCases, fetchMenusAsync } from 'features/menu/menuSlice';
import { IPage } from 'features/page/pageAPI';
import { addPageCases } from 'features/page/pageSlice';
import { addSectionCases } from 'features/page/sectionSlice';
import { IProject } from 'features/project/projectAPI';
import {
  addProjectCases,
  fetchProjectsAsync,
} from 'features/project/projectSlice';
import { IPublication } from 'features/publication/publicationAPI';
import {
  addPublicationCases,
  fetchPublicationsAsync,
} from 'features/publication/publicationSlice';
import { IMember } from 'features/team/memberAPI';
import { addMemberCases, fetchMembersAsync } from 'features/team/memberSlice';
import { IUser } from 'features/user/userAPI';
import { addUsersCases, fetchUsersAsync } from 'features/users/usersSlice';

type Status = (typeof Status)[keyof typeof Status];

export type SiteResourceKey =
  | 'users'
  | 'library'
  | 'menus'
  | 'pages'
  | 'projects'
  | 'publications'
  | 'members';
export type FetchStatus = 'idle' | 'loading' | 'failed';
export type SiteFetchStatus = Record<SiteResourceKey, FetchStatus>;

export interface ISiteState {
  users: IUser[];
  library: ILibrary;
  menus: IMenu[];
  pages: IPage[];
  projects: IProject[];
  publications: IPublication[];
  members: IMember[];
  fetchStatus: SiteFetchStatus;
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type SiteStateProps = {
  users: IUser[];
  library: ILibrary;
  menus: IMenu[];
  pages: IPage[];
  projects: IProject[];
  publications: IPublication[];
  members: IMember[];
  fetchStatus: SiteFetchStatus;
};

export function setSiteFetchStatus(
  state: ISiteState,
  resource: SiteResourceKey,
  status: FetchStatus,
) {
  state.fetchStatus[resource] = status;
}

const initialState: ISiteState = {
  users: [],
  library: {
    rootFolder: null,
    allFolders: [],
  },
  menus: [],
  pages: [],
  projects: [],
  publications: [],
  members: [],
  fetchStatus: {
    users: 'idle',
    library: 'idle',
    menus: 'idle',
    pages: 'idle',
    projects: 'idle',
    publications: 'idle',
    members: 'idle',
  },
  status: 'idle',
  errors: '',
};

export interface FetchSiteParams {
  forUsers?: boolean;
}

export const fetchSiteAsync = createAsyncThunk(
  'site/fetchSite',
  async (params: FetchSiteParams, thunkAPI) => {
    try {
      thunkAPI.dispatch(fetchUsersAsync());
      thunkAPI.dispatch(fetchFoldersAsync());
      thunkAPI.dispatch(fetchMenusAsync());
      // thunkAPI.dispatch(fetchPagesAsync());
      thunkAPI.dispatch(fetchProjectsAsync(params));
      thunkAPI.dispatch(fetchPublicationsAsync());
      thunkAPI.dispatch(fetchMembersAsync());
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    resetSite: (state) => {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    addUsersCases(builder);
    addFileCases(builder);
    addFolderCases(builder);
    addMenuCases(builder);
    addMenuItemCases(builder);
    addPageCases(builder);
    addSectionCases(builder);
    addProjectCases(builder);
    addPublicationCases(builder);
    addMemberCases(builder);
  },
});

export const getSite = (state: RootState) => state.site;

export const siteActions = siteSlice.actions;
export const { resetSite } = siteSlice.actions;

export default siteSlice.reducer;
